import type { AisPrintResultEvent } from '@medcom/shared-ui/socket/types.ts';
import type { TypedSocket } from '../socket/types.ts';

type AisPrintJob = { jobId: string; discardInMillis: number };

export type AisPrintOutcome = { state: 'printed' } | { state: 'failed'; code: string | null } | { state: 'noAnswer' };

let waiting = 0;

const warnBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = true;
};

const holdUnload = () => {
  waiting++;
  if (waiting === 1) {
    window.addEventListener('beforeunload', warnBeforeUnload);
  }
};

const releaseUnload = () => {
  waiting--;
  if (waiting === 0) {
    window.removeEventListener('beforeunload', warnBeforeUnload);
  }
};

const outcomeOf = (event: AisPrintResultEvent): AisPrintOutcome =>
  event.outcome === 'printed' ? { state: 'printed' } : { state: 'failed', code: event.code ?? null };

/**
 * Listens before the job exists, so a result that overtakes the print call is kept. The first outcome wins; the wait ends
 * with noAnswer exactly when the server drops the job. Not useSubscription: the result needs no subscription, and its
 * lifetime is the print's, not the component's.
 */
export const watchAisPrintJob = (socket: TypedSocket) => {
  const heard = new Map<string, AisPrintOutcome>();
  let jobId: string | undefined;
  let finished = false;
  let settle: (outcome: AisPrintOutcome) => void = () => {};
  let timer: ReturnType<typeof setTimeout> | undefined;

  const stop = () => {
    if (finished) {
      return;
    }
    finished = true;
    clearTimeout(timer);
    socket.off('ais-print-result', listener);
    releaseUnload();
  };

  const finish = (outcome: AisPrintOutcome) => {
    if (!finished) {
      stop();
      settle(outcome);
    }
  };

  const listener = (event: AisPrintResultEvent) => {
    if (jobId === undefined) {
      heard.set(event.jobId, outcomeOf(event));
    } else if (event.jobId === jobId) {
      finish(outcomeOf(event));
    }
  };

  holdUnload();
  socket.on('ais-print-result', listener);

  return {
    /** Resolves with the job's outcome; the wait runs from now, by the time labrador-api gave. */
    wait: (job: AisPrintJob) =>
      new Promise<AisPrintOutcome>(resolve => {
        settle = resolve;
        jobId = job.jobId;
        const early = heard.get(job.jobId);
        heard.clear();
        if (early) {
          finish(early);
        } else {
          timer = setTimeout(() => finish({ state: 'noAnswer' }), Math.max(0, job.discardInMillis));
        }
      }),
    /** For a print that never reached labrador-api. */
    cancel: stop,
  };
};
